import { useMemo, useRef } from "react";
import { FieldLines } from "./FieldLines";
import { Defender } from "./Defender";
import { ThrowArrows } from "./ThrowArrows";
import { Ball } from "./Ball";
import { Runners } from "./Runners";
import { layoutLabels } from "@/lib/labels";

const toFeet = (svg, e) => {
  const pt = svg.createSVGPoint();
  pt.x = e.clientX;
  pt.y = e.clientY;
  const p = pt.matrixTransform(svg.getScreenCTM().inverse());
  return [p.x, -p.y];
};

export const PlayField = ({ cfg, runners, positions, throws, runnerPaths, hit, playId, onTap, edit, instant, highlight, extras, still, showLabels }) => {
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const F = cfg.fence;
  const W = F * 0.72;
  const top = F * 1.02;
  const bottom = F * 0.17;
  const size = F * 0.06;
  const offsets = useMemo(
    () => layoutLabels(positions, size, { x: -W, y: -top, w: W * 2, h: top + bottom }),
    [positions, size, W, top, bottom]
  );
  const editing = !!edit;
  return (
    <svg
      ref={svgRef}
      xmlns="http://www.w3.org/2000/svg"
      fontFamily="'Barlow Condensed', Arial, Helvetica, sans-serif"
      data-testid="svg-field-canvas"
      viewBox={`${-W} ${-top} ${W * 2} ${top + bottom}`}
      className={`w-full h-auto block select-none ${editing ? "" : "cursor-crosshair"}`}
      style={{ touchAction: editing ? "none" : "manipulation" }}
      onClick={(e) => { if (!editing && onTap) onTap(toFeet(svgRef.current, e)); }}
      onPointerMove={(e) => { if (dragRef.current) edit.onDrag(dragRef.current, toFeet(svgRef.current, e)); }}
      onPointerUp={() => { dragRef.current = null; }}
      onPointerLeave={() => { dragRef.current = null; }}
    >
      <defs>
        <marker id="arrowhead" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" fill="#fff" />
        </marker>
      </defs>
      <rect x={-W} y={-top} width={W * 2} height={top + bottom} fill="var(--foul)" onPointerDown={() => editing && edit.onSelect(null)} />
      <FieldLines cfg={cfg} />
      {Object.values(positions).map((p) => (
        <Defender
          key={p.pos} pos={p.pos} pt={p.pt} job={p.job} label={p.label} size={size} showLabel={showLabels ?? !!hit}
          labelOffset={offsets[p.pos]} instant={editing || instant} editable={editing} selected={(edit?.selected ?? highlight) === p.pos} still={still}
          onGrab={(pos) => { dragRef.current = pos; edit.onSelect(pos); }}
        />
      ))}
      {hit && !hit.foul && <ThrowArrows throws={throws} players={positions} gap={size * 1.25} width={F * 0.009} playId={playId} still={still} />}
      <Runners cfg={cfg} runners={runners} paths={hit && !hit.foul ? runnerPaths : []} hitId={hit && !hit.foul ? hit.id : null} size={size} still={still} />
      {hit && !still && <Ball ball={hit.ball} size={F * 0.014} fly={hit.hitType === "fly"} hitId={hit.id} />}
      {hit && still && <circle cx={hit.ball[0]} cy={-hit.ball[1]} r={F * 0.016} fill="#fff" stroke="#dc2626" strokeWidth={F * 0.005} />}
      {extras?.(size)}
    </svg>
  );
};
